import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Typography } from '@mui/material'
import {
  LockIcon,
  FormatListBulletedIcon,
  QuestionOutlinedIcon,
  UploadIcon,
} from '@weblite/icons'
import {
  ContactPhoneOutlined,
  Devices,
  Groups,
  VisibilityOffOutlined,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { useRoom } from '$logic/room/react-hook'
import { useHall, useUserSessionsLimit } from '$logic/hall/react-hook'
import {
  COURSE_VITRINE_TAG,
  ISSUE_TAG,
  VOD_ACTIVATED_TAG,
  HIDE_MEMBER_COUNT_TAG,
  DISABLE_WATERMARK_TAG,
  SUPER_ROOM_TAG,
} from '$logic/hall/constant'
import {
  closeDefineHallAdvancedSettings,
  openAddHallMembers,
  openUserSessionsLimit,
} from '$logic/router'
import { changeHallInfo } from '$logic/hall'
import { t } from '$setup/internationalization'
import { addTagToHallTags, removeTagFromHallTags } from '$logic/hall/helper'
import { Switch } from '$common/switch'
import { OptionWithTextItem } from '$common/option-with-text-item'
import {
  PremiumFeatureType,
  showBuyPremiumFeatureDialog,
} from '$comp/modals/buy-premium-feature-dialog'
import { CommonOnboarding } from '$logic/onboarding/types'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { toLocale } from '$helper/functions/text'
import { useOpenedRoomId } from '$logic/opened-room'
import { useIsPremiumPlanEnabled } from '$logic/premium-features/react-hooks'

interface Props {
  hallId: string
  defaultHallTags: string[] | undefined
  onBack: () => unknown
  onComplete: () => unknown
}

function HallAdvancedSettings({
  onBack,
  onComplete,
  defaultHallTags,
  hallId,
}: Props) {
  const [tags, setTags] = useState<string[]>(defaultHallTags || [])

  useEffect(() => {
    setTags(defaultHallTags ?? [])
  }, [defaultHallTags])

  // TODO: Eliminate duplications with edit-hall-advanced-settings
  const { value: purchasedPremiumPlan } = useIsPremiumPlanEnabled(hallId)

  const courseVitrineEnabled = useMemo(() => {
    return tags.includes(COURSE_VITRINE_TAG)
  }, [tags])

  const issueEnabled = useMemo(() => {
    return tags.includes(ISSUE_TAG)
  }, [tags])

  const vodActivated = useMemo(() => {
    return tags.includes(VOD_ACTIVATED_TAG)
  }, [tags])

  const watermarkDisabled = useMemo(() => {
    return tags.includes(DISABLE_WATERMARK_TAG)
  }, [tags])

  const hideMemberCount = useMemo(() => {
    return tags.includes(HIDE_MEMBER_COUNT_TAG)
  }, [tags])

  const superRoomEnabled = useMemo(() => {
    return tags.includes(SUPER_ROOM_TAG)
  }, [tags])

  const [protectContent, setProtectContent] = useState(false)

  const toggleProtectContent = useCallback(() => {
    if (!protectContent && !purchasedPremiumPlan) {
      analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
        roomId: hallId,
        source: 'CREATE_HALL_HALL_SETTINGS',
        option: 'PROTECT_CONTENT',
      })
      showBuyPremiumFeatureDialog({
        featureType: PremiumFeatureType.PROTECT_CONTENT,
        hallId,
      })
      return
    }
    setProtectContent(!protectContent)
  }, [hallId, purchasedPremiumPlan, protectContent])

  const toggleTag = useCallback(
    (tag: string) => {
      const hasTag = tags.includes(tag)
      if (
        !hasTag &&
        !purchasedPremiumPlan &&
        (tag === COURSE_VITRINE_TAG ||
          tag === ISSUE_TAG ||
          tag === VOD_ACTIVATED_TAG ||
          tag === HIDE_MEMBER_COUNT_TAG)
      ) {
        analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
          roomId: hallId,
          source: 'CREATE_HALL_HALL_SETTINGS',
          option: tag,
        })
        showBuyPremiumFeatureDialog({
          featureType:
            tag === COURSE_VITRINE_TAG
              ? PremiumFeatureType.COURSE_VITRINE
              : tag === HIDE_MEMBER_COUNT_TAG
                ? PremiumFeatureType.HIDE_MEMBER_COUNT
                : PremiumFeatureType.QUESTION_AND_ANSWER,
          hallId,
        })
        return
      }

      setTags(prevTags =>
        prevTags.includes(tag)
          ? removeTagFromHallTags(tag, prevTags || [])
          : addTagToHallTags(tag, tags || []),
      )
    },
    [hallId, tags, purchasedPremiumPlan],
  )

  const onOpenUserSessionsLimit = useCallback(() => {
    if (purchasedPremiumPlan) openUserSessionsLimit(hallId)
    else {
      analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
        roomId: hallId,
        source: 'CREATE_HALL_HALL_SETTINGS',
        option: 'USER_SESSIONS_LIMIT',
      })
      showBuyPremiumFeatureDialog({
        featureType: PremiumFeatureType.USER_SESSIONS_LIMIT,
        hallId,
      })
    }
  }, [purchasedPremiumPlan, hallId])

  const handleConfirm = async () => {
    await showSaveChangesLoadingModal()
    const res = await changeHallInfo(hallId, { tags })

    await closeCurrentModal()
    if (!res) {
      closeDefineHallAdvancedSettings()
    } else onComplete()
  }

  const roomId = useOpenedRoomId()
  const { value: userSessionsLimit } = useUserSessionsLimit(roomId)

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={t(`hallSetting.channel`)}
          onConfirm={handleConfirm}
          onBack={onBack}
        />
      }
    >
      <HallAdvancedSettingsOptions
        watermarkDisabled={watermarkDisabled}
        courseVitrineEnabled={courseVitrineEnabled}
        issueEnabled={issueEnabled}
        protectContent={protectContent}
        toggleTag={toggleTag}
        toggleProtectContent={toggleProtectContent}
        vodActivated={vodActivated}
        userSessionsLimit={userSessionsLimit ?? 0}
        hideMemberCount={hideMemberCount}
        superRoomEnabled={superRoomEnabled}
        onOpenUserSessionsLimit={onOpenUserSessionsLimit}
      />
    </PageWithAppBar>
  )
}

export function DefineHallAdvancedSettings({ roomId }: { roomId: string }) {
  const room = useRoom(roomId)
  const { entity: hall } = useHall(roomId)

  if (!room || !hall) return null

  return (
    <HallAdvancedSettings
      hallId={roomId}
      onBack={closeDefineHallAdvancedSettings}
      onComplete={() => openAddHallMembers(roomId, true, true)}
      defaultHallTags={hall.tags}
    />
  )
}

const StyledOption = styled(OptionWithTextItem)`
  height: 80px;
`

const TitleWrapper = styled('div')`
  display: flex;
  background-color: ${({ theme }) => theme.palette.neutral000};
`

export function HallAdvancedSettingsOptions({
  protectContent,
  issueEnabled,
  courseVitrineEnabled,
  toggleTag,
  toggleProtectContent,
  vodActivated,
  userSessionsLimit = 0,
  hideMemberCount,
  superRoomEnabled,
  onOpenUserSessionsLimit,
  watermarkDisabled,
}: {
  protectContent: boolean
  issueEnabled: boolean
  courseVitrineEnabled: boolean
  vodActivated: boolean
  watermarkDisabled: boolean
  userSessionsLimit?: number
  toggleTag: (tag: string) => void
  toggleProtectContent: () => void
  hideMemberCount: boolean
  superRoomEnabled: boolean
  onOpenUserSessionsLimit: () => void
}) {
  const {
    enabled: courseVitrineOnboardingEnabled,
    completeOnboarding: completeCourseVitrineOnboarding,
  } = useOnboarding(CommonOnboarding.COURSE_VITRINE_SETTING_BUTTON)

  const courseVitrineButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <TitleWrapper>
        <Typography
          margin="12px 20px 4px 20px"
          variant="subtitle3"
          color="secondary700"
        >
          {t('hallAdvancedSettings.title')}
        </Typography>
      </TitleWrapper>

      <StyledOption
        ref={courseVitrineButtonRef}
        primaryIcon={
          <FormatListBulletedIcon
            sx={{ alignSelf: 'start', color: 'primary500' }}
          />
        }
        primaryText={t('chat.courseVitrine')}
        secondaryText={t('hallAdvancedSettings.courseVitrine')}
        primaryTextColor="neutral1000"
        primaryTextVariant="body2"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={
          <Switch
            checked={courseVitrineEnabled}
            disabled={false}
            onboardingEnabled={courseVitrineOnboardingEnabled}
            onboardingContainerRef={courseVitrineButtonRef}
          />
        }
        onClick={() => {
          completeCourseVitrineOnboarding()
          toggleTag(COURSE_VITRINE_TAG)
        }}
      />

      <StyledOption
        primaryIcon={
          <QuestionOutlinedIcon
            sx={{ alignSelf: 'start', color: 'primary500' }}
          />
        }
        primaryText={t('chat.generalIssue')}
        secondaryText={t('hallAdvancedSettings.issue')}
        primaryTextColor="neutral1000"
        primaryTextVariant="body2"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={<Switch checked={issueEnabled} />}
        onClick={() => toggleTag(ISSUE_TAG)}
      />

      <StyledOption
        primaryIcon={
          <LockIcon sx={{ alignSelf: 'start', color: 'primary500' }} />
        }
        primaryText={t('hallAdvancedSettings.forwardRestriction.title')}
        secondaryText={t('hallAdvancedSettings.forwardRestriction.description')}
        primaryTextColor="neutral1000"
        primaryTextVariant="body2"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={<Switch checked={protectContent} />}
        onClick={toggleProtectContent}
      />

      <StyledOption
        primaryIcon={
          <UploadIcon sx={{ alignSelf: 'start', color: 'primary500' }} />
        }
        primaryText={t('hallAdvancedSettings.vodActivation.title')}
        secondaryText={t('hallAdvancedSettings.vodActivation.description')}
        primaryTextColor="neutral1000"
        primaryTextVariant="body2"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={<Switch checked={vodActivated} />}
        onClick={() => toggleTag(VOD_ACTIVATED_TAG)}
      />

      <StyledOption
        primaryIcon={
          <VisibilityOffOutlined
            sx={{ alignSelf: 'start', color: 'primary500' }}
          />
        }
        primaryText={t('hallAdvancedSettings.hideMemberCount.title')}
        secondaryText={t('hallAdvancedSettings.hideMemberCount.description')}
        primaryTextColor="neutral1000"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={<Switch checked={hideMemberCount} />}
        onClick={() => toggleTag(HIDE_MEMBER_COUNT_TAG)}
      />

      <StyledOption
        primaryIcon={
          <Devices sx={{ alignSelf: 'start', color: 'primary500' }} />
        }
        primaryText={t('hallAdvancedSettings.userSessionsLimit.title')}
        secondaryText={t('hallAdvancedSettings.userSessionsLimit.description')}
        primaryTextColor="neutral1000"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={
          <Typography variant="subtitle3" color="primary">
            {userSessionsLimit > 0
              ? toLocale(userSessionsLimit)
              : t('common.unlimited')}
          </Typography>
        }
        onClick={onOpenUserSessionsLimit}
      />

      <StyledOption
        primaryIcon={
          <ContactPhoneOutlined
            sx={{ color: 'primary500', alignSelf: 'center' }}
          />
        }
        primaryText={t('hallAdvancedSettings.watermark.title')}
        secondaryText={t('hallAdvancedSettings.watermark.description')}
        primaryTextColor="neutral1000"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={<Switch checked={!watermarkDisabled} />}
        onClick={() => toggleTag(DISABLE_WATERMARK_TAG)}
      />

      <StyledOption
        primaryIcon={
          <Groups sx={{ color: 'primary500', alignSelf: 'center' }} />
        }
        primaryText={t('hallAdvancedSettings.superRoom.title')}
        secondaryText={t('hallAdvancedSettings.superRoom.description')}
        primaryTextColor="neutral1000"
        secondaryTextColor="neutral600"
        secondaryTextVariant="caption1"
        icon={<Switch checked={superRoomEnabled} />}
        onClick={() => toggleTag(SUPER_ROOM_TAG)}
      />
    </>
  )
}
